import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  auth,
  getCurrentUserToken,
  isAppleAuthAvailable,
  isGoogleAuthAvailable,
  signInWithApple,
  signInWithEmail,
  signInWithGoogle,
  signUpWithEmail,
} from '../../utils/firebase';
import { PixlieMark } from '../v2/ui/PixlieMark';

const BACKEND_URL =
  import.meta.env.VITE_BACKEND_URL ||
  'https://pixlie-fastapi-eu-8fb92ba4ec59.herokuapp.com';
const REQUEST_STORAGE_KEY = 'pixlie_mcp_oauth_request';

const requiredParams = [
  'client_id',
  'redirect_uri',
  'response_type',
  'code_challenge',
  'code_challenge_method',
  'resource',
] as const;

const scopeCopy: Record<string, string> = {
  'pixlie.profile': 'Identify your linked Pixlie account',
  'pixlie.assets.read': 'View your private Pixlie output assets',
  'pixlie.assets.write': 'Import only media you explicitly select',
  'pixlie.generate': 'Start generations with your existing entitlement',
  'pixlie.jobs.read': 'Read your own generation status and history',
  'pixlie.jobs.cancel': 'Cancel your own queued or processing jobs',
};

const oauthRequestSearch = (): string => {
  const current = new URLSearchParams(window.location.search);
  if (current.has('resume') || current.has('oauth_error')) {
    return sessionStorage.getItem(REQUEST_STORAGE_KEY) || '';
  }
  return window.location.search;
};

const McpConnectPage: React.FC = () => {
  const [requestSearch] = useState(oauthRequestSearch);
  const oauthParams = useMemo(() => new URLSearchParams(requestSearch), [requestSearch]);
  const [user, setUser] = useState(auth.currentUser);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(
    new URLSearchParams(window.location.search).has('oauth_error')
      ? 'Social sign-in was cancelled or could not be completed.'
      : null,
  );
  const [notice, setNotice] = useState<string | null>(null);

  const validRequest = requiredParams.every((key) => oauthParams.get(key));
  const scopes = (oauthParams.get('scope') || '')
    .split(/\s+/)
    .filter(Boolean);

  useEffect(() => auth.onAuthStateChanged(setUser), []);

  const socialRedirect = () => {
    sessionStorage.setItem(REQUEST_STORAGE_KEY, requestSearch);
    return `${window.location.origin}/oauth/connect?resume=1`;
  };

  const handleEmail = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === 'register') {
        await signUpWithEmail(email, password);
      } else {
        await signInWithEmail(email, password);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Authentication failed';
      if (message === 'CHECK_EMAIL') {
        setNotice('Check your email to verify the account, then return here and log in.');
      } else if (message === 'EMAIL_NOT_VERIFIED') {
        setError('Verify your Pixlie email first, then log in again.');
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  const finishAuthorization = async (decision: 'allow' | 'deny') => {
    if (!validRequest) return;
    setBusy(true);
    setError(null);
    try {
      const token = decision === 'allow' ? await getCurrentUserToken(true) : null;
      if (decision === 'allow' && !token) {
        throw new Error('Your Pixlie session expired. Log in again to continue.');
      }
      const payload = Object.fromEntries(oauthParams.entries());
      const response = await fetch(`${BACKEND_URL}/oauth/link/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ ...payload, decision }),
      });
      const result = (await response.json()) as {
        redirect_url?: string;
        error_description?: string;
      };
      if (!response.ok || !result.redirect_url) {
        throw new Error(result.error_description || 'Pixlie could not complete account linking.');
      }
      sessionStorage.removeItem(REQUEST_STORAGE_KEY);
      window.location.assign(result.redirect_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Pixlie account linking failed.');
      setBusy(false);
    }
  };

  if (!validRequest) {
    return (
      <div className="min-h-screen bg-[#f3eee4] text-[#1d1a16] grid place-items-center px-6">
        <div className="max-w-md rounded-[28px] border border-black/10 bg-[#fffaf0] p-8 shadow-2xl">
          <PixlieMark size={48} />
          <h1 className="mt-5 font-serif text-3xl font-bold">This connection link expired</h1>
          <p className="mt-3 text-sm text-black/60">Return to ChatGPT and connect Pixlie again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_12%_8%,#dcebdc_0,transparent_34%),radial-gradient(circle_at_88%_88%,#f0c6b6_0,transparent_32%),#f3eee4] px-5 py-8 text-[#1d1a16]">
      <main className="mx-auto w-full max-w-[540px] rounded-[30px] border border-black/10 bg-[#fffaf0] p-7 shadow-[0_28px_90px_rgba(48,40,29,0.16)] sm:p-9">
        <header className="flex items-center gap-4">
          <PixlieMark size={54} />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#a64f38]">Pixlie account</p>
            <h1 className="font-serif text-3xl font-bold">Connect to ChatGPT</h1>
          </div>
        </header>

        {error && <div className="mt-5 rounded-2xl bg-[#ffe3dc] px-4 py-3 text-sm text-[#842f20]">{error}</div>}
        {notice && <div className="mt-5 rounded-2xl bg-[#e3eee7] px-4 py-3 text-sm text-[#244d40]">{notice}</div>}

        {!user ? (
          <section className="mt-7">
            <div className="grid grid-cols-2 rounded-2xl bg-[#ebe5d9] p-1">
              {(['login', 'register'] as const).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setMode(item)}
                  className={`rounded-xl px-4 py-2.5 text-sm font-bold ${mode === item ? 'bg-white shadow-sm' : 'text-black/55'}`}
                >
                  {item === 'login' ? 'Log in' : 'Create account'}
                </button>
              ))}
            </div>
            <form onSubmit={handleEmail} className="mt-5 space-y-4">
              <label className="grid gap-1.5 text-sm font-semibold">
                Email
                <input className="rounded-2xl border border-black/15 bg-white px-4 py-3 outline-none focus:border-[#d86f52]" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
              </label>
              <label className="grid gap-1.5 text-sm font-semibold">
                Password
                <input className="rounded-2xl border border-black/15 bg-white px-4 py-3 outline-none focus:border-[#d86f52]" type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} required />
              </label>
              <button disabled={busy} className="w-full rounded-2xl bg-[#d86f52] px-4 py-3 font-bold text-white disabled:opacity-50">
                {busy ? 'Working…' : mode === 'login' ? 'Continue with Pixlie' : 'Create Pixlie account'}
              </button>
            </form>
            <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-widest text-black/40 before:h-px before:flex-1 before:bg-black/10 after:h-px after:flex-1 after:bg-black/10">or</div>
            <div className="grid gap-3 sm:grid-cols-2">
              {isGoogleAuthAvailable() && (
                <button onClick={() => void signInWithGoogle(socialRedirect())} className="rounded-2xl border border-black/15 bg-white px-4 py-3 text-sm font-bold">Continue with Google</button>
              )}
              {isAppleAuthAvailable() && (
                <button onClick={() => void signInWithApple(socialRedirect())} className="rounded-2xl bg-black px-4 py-3 text-sm font-bold text-white">Continue with Apple</button>
              )}
            </div>
          </section>
        ) : (
          <section className="mt-7">
            <div className="flex items-center justify-between rounded-2xl border border-black/10 bg-white/70 px-4 py-3">
              <span className="text-sm text-black/50">Signed in to Pixlie</span>
              <strong className="max-w-[65%] truncate text-sm">{user.email || user.displayName}</strong>
            </div>
            <div className="mt-4 rounded-[22px] bg-[#eee7db] p-5">
              <h2 className="font-serif text-xl font-bold">Allow these private Pixlie actions</h2>
              <ul className="mt-3 space-y-2 text-sm text-black/70">
                {scopes.map((scope) => <li key={scope}>• {scopeCopy[scope] || scope}</li>)}
              </ul>
              <p className="mt-4 text-xs leading-relaxed text-black/50">Only the prompt and media you choose are shared. No checkout, credit purchase, subscription change, or upgrade is available here.</p>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button disabled={busy} onClick={() => void finishAuthorization('deny')} className="rounded-2xl bg-[#e5ded2] px-4 py-3 font-bold">Cancel</button>
              <button disabled={busy} onClick={() => void finishAuthorization('allow')} className="rounded-2xl bg-[#d86f52] px-4 py-3 font-bold text-white">{busy ? 'Connecting…' : 'Allow Pixlie'}</button>
            </div>
          </section>
        )}

        <footer className="mt-6 text-center text-xs text-black/45">
          <a className="underline" href="/privacy">Privacy</a> · <a className="underline" href="/terms">Terms</a> · Brightforge Software Inc.
        </footer>
      </main>
    </div>
  );
};

export default McpConnectPage;
